import { createFileRoute } from "@tanstack/react-router"
import MarketingLayout from "~/layouts/MarketingLayout"
import { ProductHero } from "~/components/Marketing/ProductHero"
import { ProductHowItWorks } from "~/components/Marketing/ProductHowItWorks"
import { ProblemSolution } from "~/components/Marketing/ProblemSolution"
import { FeaturesGrid } from "~/components/Marketing/FeaturesGrid"
import { ProductPricingTable } from "~/components/Pricing/ProductPricingTable"
import { ProductUseCases } from "~/components/Marketing/ProductUseCases"
import { FinalCTA } from "~/components/Marketing/FinalCTA"
import { UGC_PRICING_CONFIG } from "~/config/pricing"
import { Users, Mic, Zap, Package, Palette, Video } from "lucide-react"

export const Route = createFileRoute("/ugc-ai-avatar")({
  component: UGCAIAvatarPage,
})

function UGCAIAvatarPage() {
  return (
    <MarketingLayout>
      <ProductHero
        title="Generate 50 UGC-Style Ad Videos in 10 Minutes"
        subtitle="30+ realistic AI avatars. Zero filming. Zero actors. Perfect for testing ad creative at scale."
        ctaText="Start Creating →"
        ctaHref="/auth/signin"
        secondaryText="Plans from $39/mo"
        socialProof={{
          quote: "Replaced 3 weeks of production with 2 hours",
          author: "DTC Brand Owner, $2M revenue",
        }}
      />

      <ProductHowItWorks
        title="From Product to Ads in 4 Steps"
        steps={[
          {
            number: 1,
            title: "Upload Your Product",
            bullets: ["Add product images or URL", "Paste your ad script (or generate one)"],
          },
          {
            number: 2,
            title: "Choose Avatar & Voice",
            bullets: ["30+ demographics (age, gender, ethnicity)", "Any accent or voice style"],
          },
          {
            number: 3,
            title: "Generate Variations",
            bullets: ["Creates 10 videos in 5 minutes", "Automatic lip-sync and gestures"],
          },
          {
            number: 4,
            title: "Download & Launch Ads",
            bullets: ["HD 1080p exports ready for Meta/TikTok", "Commercial license included"],
          },
        ]}
      />

      <ProblemSolution
        title="Creative Fatigue is Killing Your ROAS"
        comparisons={[
          { problem: "Finding creators: 1-2 weeks", solution: "Generate avatars instantly" },
          { problem: "Filming & editing: $500-2000/video", solution: "$2.60 per video with AI" },
          { problem: "Testing variations: Impossible at scale", solution: "50 variations before lunch" },
          { problem: "Creative burnout: Ads die in 7 days", solution: "Replace winners in minutes" },
        ]}
      />

      <FeaturesGrid
        features={[
          { title: "30+ Real Avatars", description: "Diverse demographics and styles", icon: <Users size={32} /> },
          { title: "Any Voice, Any Accent", description: "Global voice library", icon: <Mic size={32} /> },
          { title: "Instant Generate", description: "10 videos in 5 minutes", icon: <Zap size={32} /> },
          {
            title: "Commercial License",
            description: "Full rights for all platforms",
            icon: <Package size={32} />,
          },
          {
            title: "Background Options",
            description: "Customize scenes and settings",
            icon: <Palette size={32} />,
          },
          {
            title: "HD Export 1080p",
            description: "Professional quality exports",
            icon: <Video size={32} />,
          },
        ]}
        columns={3}
      />

      <ProductPricingTable config={UGC_PRICING_CONFIG} />

      <ProductUseCases
        title="Built for Performance Marketers"
        useCases={[
          "Test 10 angles before spending ad budget",
          "Replace burned-out creative in minutes",
          "Scale winning campaigns with variations",
          "Launch without hiring creators",
        ]}
      />

      <FinalCTA
        title="Stop Waiting on Creators. Start Testing Now."
        ctaText="Create Your First Video →"
        ctaHref="/auth/signin"
        supportingText="No filming. No actors. No waiting."
      />
    </MarketingLayout>
  )
}
